/**
 * Shared HTTP failure policy: which statuses mean "try again later" and how long the server
 * asked us to wait. Both the TanStack Query retry rules (`query/queryClient.ts`) and the
 * refresh classifier (`refreshOutcome.ts`) read it, so the two cannot drift apart.
 */
export const HTTP_REQUEST_TIMEOUT = 408;
export const HTTP_TOO_MANY_REQUESTS = 429;
const HTTP_SERVER_ERROR_MIN = 500;

/** The two client (4xx) statuses that mean "retry later" rather than "this request is refused". */
export const RETRYABLE_CLIENT_ERROR_STATUSES: ReadonlySet<number> = new Set([
  HTTP_REQUEST_TIMEOUT,
  HTTP_TOO_MANY_REQUESTS,
]);

export function isServerErrorStatus(status: number): boolean {
  return status >= HTTP_SERVER_ERROR_MIN;
}

/** Statuses that say nothing about the request's validity: 408 and 5xx (429 is handled apart). */
export function isTransientStatus(status: number): boolean {
  return isServerErrorStatus(status) || status === HTTP_REQUEST_TIMEOUT;
}

const MS_PER_SECOND = 1000;
const RETRY_AFTER_HEADER = 'retry-after';
const RETRY_AFTER_DELTA_SECONDS = /^\d+$/;
// IMF-fixdate, the only HTTP-date form RFC 9110 requires senders to use. Date.parse alone also
// accepts junk such as "1.5", which would turn a malformed header into a plausible delay.
const RETRY_AFTER_IMF_FIXDATE = /^[A-Za-z]{3}, \d{2} [A-Za-z]{3} \d{4} \d{2}:\d{2}:\d{2} GMT$/;

/**
 * Parses `Retry-After` (delta-seconds or HTTP-date) to milliseconds from now. Missing or
 * malformed values return `undefined` so the caller applies its own fallback.
 */
export function parseRetryAfterMs(value: string | undefined, now: number = Date.now()): number | undefined {
  const text = value?.trim();
  if (!text) return undefined;
  if (RETRY_AFTER_DELTA_SECONDS.test(text)) return Number(text) * MS_PER_SECOND;
  if (!RETRY_AFTER_IMF_FIXDATE.test(text)) return undefined;
  const at = Date.parse(text);
  return Number.isNaN(at) ? undefined : Math.max(0, at - now);
}

/** Reads `Retry-After` from a plain header object (any case) or an AxiosHeaders-style `get()`. */
export function readRetryAfterMs(headers: unknown): number | undefined {
  if (!headers || typeof headers !== 'object') return undefined;
  const bag = headers as { get?: (n: string) => unknown } & Record<string, unknown>;
  const raw =
    typeof bag.get === 'function'
      ? bag.get(RETRY_AFTER_HEADER)
      : Object.entries(bag).find(([k]) => k.toLowerCase() === RETRY_AFTER_HEADER)?.[1];
  return parseRetryAfterMs(typeof raw === 'string' ? raw : undefined);
}
